import LazyLoad from 'vanilla-lazyload';
import AOS from 'simple-aos';
import { Collapse } from 'bootstrap'; // eslint-disable-line no-unused-vars
import lottie from 'lottie-web';
import Swiper from 'swiper';
import { Autoplay, Mousewheel, FreeMode } from 'swiper/modules';
import { getBootstrapBreakpoints, isInViewport } from '../global/functions';
import '../components/parallax';
import createTextReveal from '../components/textReveal.js';

const breakpoints = getBootstrapBreakpoints();
// initialize lazyload
const initLazyLoad = new LazyLoad();
initLazyLoad.update();

// initialize AOS
AOS.init({
	disable: window.matchMedia('(prefers-reduced-motion: reduce)'), // reduce motion when user has set prefers-reduced-motion for accessibility
	easing: 'ease-in-out',
	duration: 800,
	offset: 80,
	anchorPlacement: 'top-bottom',
	once: true,
});

const animateText = () => {
	const featuresTexts = document.querySelectorAll('.features-text span');
	let index = 0;
	const textInTimer = 1600;
	const textOutTimer = 1400;

	const animate = () => {
		// Remove classes from all elements
		featuresTexts.forEach((text) =>
			text.classList.remove('text-in', 'text-out'),
		);

		// Add 'text-in' class to current element
		featuresTexts[index].classList.add('text-in');

		// Schedule 'text-out' class
		setTimeout(() => {
			featuresTexts[index].classList.add('text-out');
		}, textOutTimer);

		// Schedule next animation
		setTimeout(() => {
			index = (index + 1) % featuresTexts.length;
			animate();
		}, textInTimer);
	};
	// Start the animation
	animate();
};
document.addEventListener('DOMContentLoaded', animateText);

/*
 * * Features swiper
 */
/* eslint-disable no-new */

const featuresSwiper = new Swiper('.features-swiper', {
	modules: [Mousewheel, FreeMode, Autoplay],
	slidesPerView: 'auto',
	spaceBetween: 30,
	breakpoints: {
		// Mobile settings (up to 435px)
		0: {
			autoHeight: true,
			slidesPerView: 'auto',
			loop: true,
			speed: 8000, // Much slower speed for smoother movement
			autoplay: {
				enabled: true,
				delay: 0,
				disableOnInteraction: false,
			},
			freeMode: false, // Disable freeMode on mobile completely
			mousewheel: {
				enabled: false, // Disable mousewheel on mobile
			},
		},
		// Desktop settings (436px and above)
		436: {
			autoHeight: true,
			slidesPerView: 'auto',
			loop: false,
			freeMode: {
				enabled: true,
			},
			mousewheel: {
				enabled: true,
				forceToAxis: true,
				sensitivity: 1.5,
				thresholdDelta: 1,
				thresholdTime: 150,
			},
			autoplay: {
				enabled: false,
			},
		},
	},
});
/*
 * * Change the src of the accordion media
 * * according to the data-media attribute
 * * on the selected accordion
 */

const accordion = document.querySelector('.accordion');
const accordionMedia = document.querySelector('.accordion-media');
const accordionButton = accordion.querySelector('.accordion-button');
const accordionContent = accordion.querySelectorAll('.accordion-content');
const animations = {};
const animationUrls = [];
let animationsPreloaded = false;
let initAnimation;

accordionContent.forEach((e) => {
	e.getAttribute('data-media');
	animationUrls.push(e.getAttribute('data-media'));
});

// preload all the animations when scrolling down to section
document.addEventListener('scroll', () => {
	const preloadSection = document.querySelector('.features');
	if (isInViewport(preloadSection, 800) && !animationsPreloaded) {
		animationsPreloaded = true;
		animationUrls.forEach((url, index) => {
			fetch(url)
				.then((response) => response.json())
				.then((data) => {
					animations[`animation${index + 1}`] = data;
				});
		});
	}
});

// Initialize the animation on page load
initAnimation = lottie.loadAnimation({
	container: accordionMedia,
	renderer: 'svg',
	loop: false,
	autoplay: true,
	path: accordionMedia.getAttribute('data-animation'), // Default animation
});

accordion.addEventListener('show.bs.collapse', (e) => {
	// Destroy the previous animation
	initAnimation.destroy();

	// Update the animation path dynamically based on active accordion
	let nextAnimationIndex = e.target.getAttribute('data-acc');
	nextAnimationIndex = parseInt(nextAnimationIndex, 10) + 1;

	initAnimation = lottie.loadAnimation({
		container: accordionMedia,
		renderer: 'svg',
		loop: false,
		autoplay: true,
		animationData: animations[`animation${nextAnimationIndex}`],
		rendererSettings: {
			progressiveLoad: false, // Boolean, only svg renderer, loads dom elements when needed. Might speed up initialization for large number of elements.
		},
	});
	accordionMedia.classList.remove('d-none');

	if (breakpoints.xs || breakpoints.sm || breakpoints.md) {
		e.target.appendChild(accordionMedia);
		accordionMedia.classList.add('d-block');
	}
});

// Remove the collapsed class and show class to close the accordion on mobile
if (breakpoints.xs || breakpoints.sm || breakpoints.md) {
	accordionButton.classList.add('collapsed');
	accordionContent.forEach((e) => {
		e.classList.remove('show');
	});
}

// on accordion change event add font weight medium to the active tab
const accordionButtons = document.querySelectorAll('.accordion-button');

accordionButtons.forEach((button) => {
	button.addEventListener('click', () => {
		const currentlyActive = document.querySelector(
			'.accordion-button.font-weight-medium',
		);
		if (currentlyActive && currentlyActive !== button) {
			currentlyActive.classList.remove('font-weight-medium');
			currentlyActive.classList.add('font-weight-light');
		}
		button.classList.toggle('font-weight-medium');
		button.classList.toggle('font-weight-light');
	});
});

/*
 * * logos-media-swiper
 */

const logosMedia = document.querySelector('.logos-media-swiper');
const logosMediaNumberOfSlides =
	logosMedia.querySelectorAll('.swiper-slide').length;
const logosMediaSwiper = new Swiper('.logos-media-swiper', {
	modules: [Autoplay],
	slidesPerView: 2,
	spaceBetween: 0,
	slidesPerGroup: 1,
	loop: true,
	speed: 3000,
	autoplay: {
		delay: 0,
		disableOnInteraction: false,
	},
	allowTouchMove: true,
	freeMode: {
		enabled: true,
		momentum: false,
	},
	loopAdditionalSlides: 2,
	breakpoints: {
		436: {
			slidesPerView: 3,
		},
		768: {
			slidesPerView: 5,
		},
		1024: {
			slidesPerView: logosMediaNumberOfSlides || 'auto',
			autoplay: {
				delay: 2000,
			},
			freeMode: {
				enabled: false,
			},
			loop: false,
		},
	},
});

/*
 ** Text Reveal Animation from gray to white
 ** on scroll or after 3 seconds
 */
const customTextReveal = createTextReveal({
	selector: '.intro-text',
	delay: 30,
	autoStartDelay: 0,
	fromColor: 'var(--tbla-gray-500)',
	toColor: 'var(--tbla-white)',
});
